import React from "react"
import { useHistory } from "react-router-dom"
import type { BookingTrackingCallbacks, ScreeningProgram } from "@internal/telemedicine"
import { createBookingTrackingStore, type BookingEntrySource, type FormSubmissionAction } from "./bookingTracking"
import { MemberActions } from "./memberActions"
import { useMemberActionTracking } from "./useMemberActionTracking"

const BookingTrackingContext = React.createContext<ReturnType<typeof createBookingTrackingStore> | null>(null)

export function BookingTrackingProvider({ children }: React.PropsWithChildren): JSX.Element {
  const { mutate } = useMemberActionTracking()
  const mutateRef = React.useRef(mutate)
  mutateRef.current = mutate
  const [store] = React.useState(() => createBookingTrackingStore((action) => mutateRef.current(action)))
  const history = useHistory()
  React.useEffect(() => history.listen((location) => store.navigate(location.pathname)), [history, store])
  return <BookingTrackingContext.Provider value={store}>{children}</BookingTrackingContext.Provider>
}

function useBookingTrackingStore() {
  return React.useContext(BookingTrackingContext)
}

export function useBookingActionTracking() {
  const store = useBookingTrackingStore()
  const trackBookingEntry = (event: React.MouseEvent, source: BookingEntrySource, program?: ScreeningProgram) => {
    // Modified clicks remain real anchors. The new document starts its own direct flow.
    const target = event.currentTarget.getAttribute("target")
    if (
      !store ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target && target !== "_self")
    )
      return
    if (source === "provider_finder" && program) store.selectProgram(program, source)
    else store.start(source, program)
  }
  return { trackBookingEntry, trackPrepareForVisit: store?.prepare, endBookingFlow: store?.end }
}

export function useBookingFlowTracking(program: ScreeningProgram): BookingTrackingCallbacks | undefined {
  const store = useBookingTrackingStore()
  const binding = React.useMemo(() => {
    if (!store) return undefined
    let callbacks: BookingTrackingCallbacks | undefined
    // Bind once so late booking responses still belong to their original flow.
    const initialize = () => (callbacks ??= store.selectProgram(program).callbacks)
    const tracking: BookingTrackingCallbacks = {
      onEvent: (event) => initialize().onEvent(event),
      onBookingSubmitted: (appointmentType) => initialize().onBookingSubmitted(appointmentType)
    }
    return { initialize, tracking }
  }, [store, program])
  // Direct entry is recorded on mount, even while booking data loads. Child effects may bind first.
  React.useLayoutEffect(() => {
    binding?.initialize()
  }, [binding])
  return binding?.tracking
}

export function usePreVisitFormTracking() {
  const store = useBookingTrackingStore()
  const { mutate: recordAction } = useMemberActionTracking()
  return (appointmentId: string, submissionAttemptId: string) => {
    // Capture once per request so a late response retains its original flow after navigation.
    const trackForFlow = store?.getFormSubmissionTracker(appointmentId)
    const track = (memberActionTaken: FormSubmissionAction) => {
      try {
        if (trackForFlow) trackForFlow(memberActionTaken, submissionAttemptId)
        else recordAction({ memberActionTaken, metaData: { appointmentId, submissionAttemptId } })
      } catch {
        // Tracking must not prevent submission or turn a successful response into a form error.
      }
    }
    track(MemberActions.PRE_VISIT_FORM_SUBMISSION_ATTEMPTED)
    return () => track(MemberActions.PRE_VISIT_FORM_SUBMISSION_SUCCEEDED)
  }
}
